import {
    Flex,
    Box,
    Button,
    Select,
    //Checkbox,
    useMultiStyleConfig,
    Text,
    Link,
} from '@chakra-ui/react'
import Checkbox from '../../../../components/checkbox'
import {ChevronDown} from '../../../../components/icons'
import PropTypes from 'prop-types'
import React, {useCallback, useMemo, useState, useEffect} from 'react'
import {FormattedMessage, useIntl} from 'react-intl'
import ItemList from '../../../../components/item-list'
import {useForm, useWatch} from 'react-hook-form'
import OrderItemCard from './order-item-card'
import {remCalc} from '../../../../theme/_utils'
import {useOrigin} from '@salesforce/pwa-kit-react-sdk/ssr/universal/hooks'
import {addDays} from '../../../../utils/utils'
import {MRT_API_BASEPATH} from '../../../../ssr/constants'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import ReturnOrderConfirmationModal from './return-order-confirmation-modal'
import PricingDetailsReturnOrder from './pricing-details-return-order'
import LoadingSpinner from '@salesforce/retail-react-app/app/components/loading-spinner'

const ReturnOrderForm = ({
    order,
    /*onOpen*/ orderId,
    deliveryInfo,
    activeShipment,
    orderType,
    countrySettings,
    returnOrders,
    setActiveShipment,
    refetchOrder,
}) => {
    // const dispatch = useContext(ActionsContext)
    const appOrigin = useOrigin(false)
    const {buildUrl} = useMultiSite()
    const {formatMessage} = useIntl()
    const styles = useMultiStyleConfig('Orders')
    const [isModalOpen, setIsModalOpen] = useState(false)
    const [isReturnFetchLoading, setIsReturnFetchLoading] = useState(false)
    const minimumDaysReturn = countrySettings?.MinimumDaysForReturn__c
    const maximumDaysReturn = countrySettings?.MaximumDaysForReturn__c
    const today = new Date()

    const {handleSubmit, control, setValue, register} = useForm({
        defaultValues: {
            items: activeShipment?.orderItems?.map((order) => ({
                id: order.Id,
                quantity: order.QuantityAvailableToReturn,
                selected: false,
                returnReason: '',
            })),
        },
    })

    const handleCheckboxChange = (index, isChecked) => {
        if (!isChecked) setValue(`items.${index}.selected`, '')
        setValue(`items.${index}.selected`, isChecked)
    }

    const handleModalClose = () => {
        setIsModalOpen(false)
        setActiveShipment(null)
    }

    const orderItems = useWatch({
        control,
        name: 'items',
    })

    const options = useMemo(
        () => [
            {
                label: formatMessage({
                    id: 'account_order_detail.options.wrong_item_delivered',
                    defaultMessage: 'Wrong item delivered',
                }),
                value: '01',
            },
            {
                label: formatMessage({
                    id: 'account_order_detail.options.defective_item',
                    defaultMessage: 'Defective item',
                }),
                value: '02',
            },
            {
                label: formatMessage({
                    id: 'account_order_detail.options.damaged_item',
                    defaultMessage: 'Damaged item',
                }),
                value: '03',
            },
            {
                label: formatMessage({
                    id: 'account_order_detail.options.damaged_item_and_box',
                    defaultMessage: 'Item and box both damaged',
                }),
                value: '04',
            },
            {
                label: formatMessage({
                    id: 'account_order_detail.options.missings_parts',
                    defaultMessage: 'Missing products/spares',
                }),
                value: '05',
            },
            {
                label: formatMessage({
                    id: 'account_order_detail.options.change_of_mind',
                    defaultMessage: 'Change of mind',
                }),
                value: '06',
            },
            {
                label: formatMessage({
                    id: 'account_order_detail.options.better_price_available',
                    defaultMessage: 'Better price available',
                }),
                value: '07',
            },
            {
                label: formatMessage({
                    id: 'account_order_detail.options.innacurate_description',
                    defaultMessage: 'Inaccurate website description',
                }),
                value: '08',
            },
            {
                label: formatMessage({
                    id: 'account_order_detail.options.late_delivery',
                    defaultMessage: 'Late delivery',
                }),
                value: '09',
            },
        ],
        [],
    )

    const isItemAlreadyReturned = (itemId) => {
        // Only consider as already returned if the associated return order's status is not Declined
        return returnOrders?.some(
            (returnOrder) =>
                returnOrder?.Status?.toLowerCase() !== 'declined' &&
                returnOrder?.ReturnOrderLineItems?.records.some(
                    (lineItem) => lineItem.OrderItemSummaryId === itemId,
                ),
        )
    }

    const onSubmit = useCallback(async (formData) => {
        // formData.preventDefault()
        const selectedReturns = formData?.items
            ?.filter((order) => order.selected)
            ?.map(({id: orderItemSummaryId, quantity, returnReason: reasonForReturn}) => ({
                orderItemSummaryId,
                quantity,
                reasonForReturn,
            }))

        if (selectedReturns?.length > 0) {
            ;(async () => {
                try {
                    setIsReturnFetchLoading(true)

                    const payload = {
                        inputs: [
                            {
                                ReturnInputDescriptor: {
                                    orderSummaryId: orderId,
                                    labelFlag: 'Y', // ask tommaso about this value as I dont remember
                                    shipFromCity: deliveryInfo?.DeliverToCity,
                                    shipFromStreet: deliveryInfo?.DeliverToStreet,
                                    shipFromCountryCode: deliveryInfo?.DeliverToCountryCode,
                                    shipFromStateCode: deliveryInfo?.DeliverToStateCode,
                                    shippingStreetNumber: deliveryInfo?.DeliverAddressNumber__c
                                        ? deliveryInfo.DeliverAddressNumber__c // Retrieves the street number from the delivery address if available
                                        : deliveryInfo?.DeliverToStreet?.match(/\d+/)?.[0] || '', // Otherwise retrieves the first number found in the street address
                                    returnItems: selectedReturns,
                                },
                            },
                        ],
                    }

                    const response = await fetch(
                        `${appOrigin}${MRT_API_BASEPATH}/return-order-oms`,
                        {
                            method: 'POST',
                            headers: {
                                'Content-Type': 'application/json',
                            },
                            body: JSON.stringify(payload),
                        },
                    )

                    const result = await response.json()
                    setIsReturnFetchLoading(false)
                    if (result.success) {
                        setIsModalOpen(true)
                        // TODO open modal and possibly (refetch order) using refetchOrder from useOrderDetails hook look at OrderSummarySection for similar example
                        // When closing the modal make sure to update the activeShipment id using setActiveShipment to undefined/null/empty string => example in MultipleShipments component
                        // NOTE: when activeShipment is updated it will cause a rerender(because its in the context) so refetchOrder may be not necessary here - test it
                    }
                } catch (error) {
                    console.error('Error during return order creation:', error)
                    setIsReturnFetchLoading(false)
                } finally {
                    setIsReturnFetchLoading(false)
                    // setIsLoading(false)
                }
            })()
        }
    }, [])

    const splitItemsForReturn = useMemo(() => {
        return activeShipment?.orderItems?.flatMap((orderItem) => {
            const fulfillmentItem = activeShipment?.FulfillmentOrderLineItems?.records.find(
                (fulfillmentItem) => fulfillmentItem.OrderItemSummaryId === orderItem.Id,
            )
            const cleanedDate = fulfillmentItem?.DeliveryDate__c
                ? fulfillmentItem.DeliveryDate__c.replace(/(\+|-)(\d{2})(\d{2})$/, '$1$2:$3')
                : null
            const baseDate = cleanedDate ? new Date(cleanedDate) : null

            const alreadyReturned = isItemAlreadyReturned(orderItem.Id)
            const canBeReturned =
                !orderItem.notReturnable &&
                orderItem.QuantityAvailableToReturn > 0 &&
                orderItem.PurchaseType__c !== 'Subscription' &&
                order.purchaseType != 'HO' &&
                order.purchaseType != 'HC' &&
                orderType === 'Standard' &&
                today >= addDays(minimumDaysReturn, baseDate) &&
                today <= addDays(maximumDaysReturn, baseDate)

            const items = []

            if (alreadyReturned) {
                items.push({
                    ...orderItem,
                    deliveryDate: baseDate,
                    alreadyReturned: true,
                    canBeReturned: false,
                })
            }

            if (canBeReturned) {
                items.push({
                    ...orderItem,
                    deliveryDate: baseDate,
                    alreadyReturned: false,
                    canBeReturned: true,
                })
            }

            // If neither alreadyReturned nor canBeReturned, still push a "not returnable" entry
            if (!alreadyReturned && !canBeReturned) {
                items.push({
                    ...orderItem,
                    deliveryDate: baseDate,
                    alreadyReturned: false,
                    canBeReturned: false,
                    notReturnable: true,
                })
            }

            return items
        })
    }, [activeShipment, isItemAlreadyReturned, minimumDaysReturn, maximumDaysReturn, today])

    // Ensure every item in splitItemsForReturn has a corresponding entry in orderItems (form state)
    // If not, add a default entry (for already returned items, etc.)
    useEffect(() => {
        // Defensive copy to avoid mutating state directly
        let changed = false
        const updatedOrderItems = [...(orderItems || [])]
        splitItemsForReturn?.forEach((item, idx) => {
            let oi = updatedOrderItems[idx]
            // If missing or missing id/quantity, fill from duplicate or from item
            if (!oi || !oi.id || oi.quantity === undefined) {
                // Try to find a previous entry with same item.Id
                const duplicate = updatedOrderItems.find(
                    (o, i) => i !== idx && o && o.id === item.Id,
                )
                updatedOrderItems[idx] = {
                    id: item.Id,
                    quantity:
                        item.QuantityAvailableToReturn !== undefined
                            ? item.QuantityAvailableToReturn
                            : (duplicate && duplicate.quantity) || 1,
                    selected: oi?.selected || false,
                    returnReason: oi?.returnReason || '',
                }
                changed = true
            }
        })
        if (changed) {
            setValue('items', updatedOrderItems)
        }
    }, [splitItemsForReturn, orderItems, setValue])

    const isSubmitDisabled =
        !orderItems?.some((order) => order.selected) ||
        orderItems?.some((order) => order.selected && !order.returnReason)

    return (
        <form onSubmit={handleSubmit(onSubmit)}>
            <Flex
                flexDirection={'column'}
                gap={{base: remCalc(24), lg: remCalc(40)}}
                data-testid="form"
            >
                <Flex flexDirection={'column'} {...styles.borderWrapper}>
                    <ItemList
                        items={splitItemsForReturn}
                        renderItem={(item, index) => {
                            return (
                                <Flex
                                    key={index}
                                    px={remCalc(24)}
                                    {...styles.returnOrderFormContainer}
                                >
                                    <Flex {...styles.returnOrderItemContainer}>
                                        {item.canBeReturned && (
                                            <Checkbox
                                                {...register(`items.${index}.selected`)}
                                                name={`items.${index}.selected`}
                                                onChange={(e) =>
                                                    handleCheckboxChange(index, e.target.checked)
                                                }
                                                aria-label="select order checkbox"
                                                data-testid="checkbox"
                                            />
                                        )}
                                        <OrderItemCard
                                            item={item}
                                            isMultipleShipment={true}
                                            editQuantity={
                                                orderItems?.[index]?.selected &&
                                                item.QuantityAvailableToReturn > 1
                                            }
                                            isReturnOrderForm={true}
                                            controlled={
                                                item.canBeReturned && {
                                                    quantity: orderItems?.[index]?.quantity,
                                                    setQuantity: (quantity) => {
                                                        setValue(
                                                            `items.${index}.quantity`,
                                                            quantity,
                                                        )
                                                    },
                                                }
                                            }
                                        />
                                    </Flex>
                                    {item.canBeReturned ? (
                                        <Box {...styles.returnOrderSelectContainer}>
                                            <Select
                                                {...register(`items.${index}.returnReason`)}
                                                isDisabled={!orderItems?.[index]?.selected}
                                                placeholder={formatMessage({
                                                    id: 'account_order_detail.select_return_reason_placeholder',
                                                    defaultMessage: 'Select reason',
                                                })}
                                                isRequired
                                                icon={<ChevronDown />}
                                                data-testid="select"
                                                {...styles.returnOrderSelect}
                                            >
                                                {options.map((opt) => (
                                                    <option key={opt?.value} value={opt?.value}>
                                                        {opt?.label}
                                                    </option>
                                                ))}
                                            </Select>
                                        </Box>
                                    ) : item.alreadyReturned ? (
                                        <Flex
                                            {...styles.returnFormWarningItemAlreadyReturnedContainer}
                                        >
                                            <Flex
                                                {...styles.returnFormWarningItemAlreadyReturnedInnerContainer}
                                            >
                                                <Text {...styles.returnFormWarningTitle}>
                                                    <FormattedMessage
                                                        id="return_label_form_not_returnable_already_returned"
                                                        defaultMessage="Prodotto già restituito"
                                                    />
                                                </Text>
                                            </Flex>
                                        </Flex>
                                    ) : (
                                        <Flex {...styles.returnFormWarningContainer}>
                                            <Flex {...styles.returnFormWarningInnerContainer}>
                                                <Text {...styles.returnFormWarningTitle}>
                                                    <FormattedMessage
                                                        id="return_label_form_not_returnable"
                                                        defaultMessage="Prodotto non restituibile"
                                                    />
                                                </Text>
                                                <Text {...styles.returnFormWarningDescription}>
                                                    <FormattedMessage
                                                        id="return_label_form_not_returnable_message"
                                                        defaultMessage="Il diritto di recesso non può essere esercitato per questo prodotto in quanto non è restituibile."
                                                    />
                                                </Text>
                                            </Flex>
                                            <Flex {...styles.returnFormWarningLinkContainer}>
                                                <Button
                                                    as={Link}
                                                    href={buildUrl('/contact-us')}
                                                    {...styles.tertiaryButton}
                                                >
                                                    <FormattedMessage
                                                        id="account_order_detail.label.contact_us"
                                                        defaultMessage="Contact us"
                                                    />
                                                </Button>
                                            </Flex>
                                        </Flex>
                                    )}
                                    <ReturnOrderConfirmationModal
                                        isOpen={isModalOpen}
                                        onClose={handleModalClose}
                                        refetchOrder={refetchOrder}
                                    />
                                </Flex>
                            )
                        }}
                    />
                    <PricingDetailsReturnOrder
                        order={order}
                        selectedItems={orderItems?.filter((item) => item.selected)}
                    />
                </Flex>
                {isReturnFetchLoading ? (
                    <LoadingSpinner />
                ) : (
                    <Button
                        type="submit"
                        isDisabled={isSubmitDisabled}
                        variant={'primary'}
                        width={{base: '100%', lg: remCalc(332)}}
                        right="0"
                        alignSelf={'flex-end'}
                    >
                        <FormattedMessage
                            id="account_order_detail.label.return_items"
                            defaultMessage="Return items"
                        />
                    </Button>
                )}
            </Flex>
        </form>
    )
}

ReturnOrderForm.propTypes = {
    order: PropTypes.object,
    // onOpen: PropTypes.func,
    orderId: PropTypes.string,
    deliveryInfo: PropTypes.object,
    activeShipment: PropTypes.object,
    orderType: PropTypes.string,
    countrySettings: PropTypes.any,
    returnOrders: PropTypes.any,
    setActiveShipment: PropTypes.any,
    refetchOrder: PropTypes.func,
}
ReturnOrderForm.displayName = 'order-order-form'
export default ReturnOrderForm
